/* eslint-disable @typescript-eslint/no-explicit-any */
import { Paragraph, ITimeMachine } from '@treams/treams-ui';
import { Link } from '@treams/treams-routes';
import { kebabCase } from 'lodash';
import { rgba } from 'polished';
import { PropsWithChildren } from 'react';
import { Box } from 'reflexbox';
import styled from 'styled-components';

import * as graphql from 'generated/graphql';

import { AnalyticsConversationsMetric } from './AnalyticsConversationsMetric';
import { AnalyticsGraphMetric } from './AnalyticsGraphMetric';
import { AnalyticsNumberMetric } from './AnalyticsNumberMetric';

export const AnalyticsMetricContainer = styled(Box)`
	:not(:last-child) {
		border-bottom: 1px solid ${(props) => props.theme.colors.lightBlue};
	}
`;

const AnalyticsMetricLinkElement = styled(Link)`
	display: block;
	position: relative;
	margin: 4px -9px;
	padding: 0 9px;
	border-radius: 4px;

	:hover {
		${Paragraph} {
			color: ${(props) => props.theme.colors.primary};
		}

		background-color: ${(props) => rgba(props.theme.colors.primary, 0.1)};
	}

	:active {
		background-color: ${(props) => rgba(props.theme.colors.primary, 0.15)};
	}
`;

export function AnalyticsMetricLink(
	props: PropsWithChildren<{
		metric: graphql.AnalyticsMetric;
		timeMachine?: ITimeMachine;
	}>,
) {
	const link = props.metric.id.split('.').map(kebabCase).join('/');

	const queryString = props.timeMachine
		? `?interval=${props.timeMachine.interval}&cursor=${props.timeMachine.urlCursor}`
		: '';

	return (
		<AnalyticsMetricLinkElement to={`/analytics/v2/metric/${link}${queryString}`}>
			{props.children}
		</AnalyticsMetricLinkElement>
	);
}

export function valueFromMetric(metric: any): number | undefined {
	return metric.__typename === 'AnalyticsIntegerMetric'
		? metric.integer
		: metric.__typename === 'AnalyticsFloatMetric'
		? metric.float
		: metric.__typename === 'AnalyticsPercentageMetric'
		? metric.percentage
		: metric.__typename === 'AnalyticsFractionMetric' ||
		  metric.__typename === 'AnalyticsConversationTemplateMetric'
		? metric.value
		: undefined;
}

export function AnalyticsMetric(props: {
	metricGroupID: string;
	metric: graphql.AnalyticsMetric;
	totalUsers: number;
	timeMachine: ITimeMachine;
}) {
	switch (props.metric.__typename) {
		case 'AnalyticsIntegerMetric':
		case 'AnalyticsFloatMetric':
		case 'AnalyticsPercentageMetric':
		case 'AnalyticsFractionMetric':
			return (
				<AnalyticsMetricLink
					metric={props.metric as graphql.AnalyticsMetric}
					timeMachine={props.timeMachine}
				>
					<AnalyticsNumberMetric
						metricGroupID={props.metricGroupID}
						metric={props.metric as any}
						totalUsers={props.totalUsers}
					/>
				</AnalyticsMetricLink>
			);
		case 'AnalyticsGraphMetric':
			return (
				<AnalyticsGraphMetric
					metricGroupID={props.metricGroupID}
					metric={props.metric as graphql.AnalyticsGraphMetric}
				/>
			);
		case 'AnalyticsConversationsMetric':
			return (
				<AnalyticsConversationsMetric
					metricGroupID={props.metricGroupID}
					metric={props.metric as graphql.AnalyticsConversationsMetric}
					totalUsers={props.totalUsers}
				/>
			);
	}
	return null;
}
